import {
    AuthSession,
    signOut as amplifySignOut,
    confirmSignIn,
    fetchAuthSession,
    getCurrentUser,
    signIn,
    signInWithRedirect,
} from 'aws-amplify/auth';
import axios from 'axios';

import type { LoadUserPropertiesResult } from '@/models/auth.ts';
import { LoadUserPropertiesStatus } from '@/models/auth.ts';

import { cognitoTokenPayloadSchema } from '@/schemas/auth-schemas.ts';

import { ServiceUrl } from '@/services/service-url.ts';

import { isDevEnvironment, isEternoDomain, isLocalEnvironment } from '@/utils/env-utils.ts';

export enum SubDomain {
    ADMINS = 'admins',
    OFFICE = 'office',
    PRACTITIONERS = 'practitioners',
    PROFESSIONALS = 'professionals',
}

export const googleSignIn = async (): Promise<void> => {
    await signInWithRedirect({
        provider: 'Google',
    });
};

interface MagicLinkPayload {
    email: string;
    domain: string;
    subdomain: SubDomain;
    is_white_label: boolean;
}

const buildMagicLinkPayload = (email: string): MagicLinkPayload => ({
    email: email,
    domain: isLocalEnvironment() ? 'eterno-health.io' : globalThis.location.hostname.split('.').slice(-2).join('.'),
    subdomain: isLocalEnvironment() || isDevEnvironment() ? SubDomain.PRACTITIONERS : SubDomain.PROFESSIONALS,
    is_white_label: !isEternoDomain(),
});

export const createMagicLink = async (email: string): Promise<void> => {
    await axios.post(ServiceUrl.PRACTITIONER_API_DOMAIN.PUBLIC.MAGIC_LINK.CREATE, buildMagicLinkPayload(email));
};

// Demo login: same magic-link endpoint, but with a shared demo password.
// The emailed magic-link signs in the user via an unchanged /magic-link callback.
export const createDemoMagicLink = async (email: string, demoPassword: string): Promise<void> => {
    await axios.post(ServiceUrl.PRACTITIONER_API_DOMAIN.PUBLIC.MAGIC_LINK.CREATE, {
        ...buildMagicLinkPayload(email),
        demo_password: demoPassword,
        is_demo_login: true,
    });
};

export const magicLinkSignIn = async (
    email: string,
    token: string
): Promise<{ user: Awaited<ReturnType<typeof getCurrentUser>>; properties: LoadUserPropertiesResult }> => {
    const { nextStep } = await signIn({
        username: email,
        options: {
            authFlowType: 'CUSTOM_WITHOUT_SRP',
        },
    });

    if (nextStep.signInStep !== 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        throw new Error(`Unexpected sign-in step: ${nextStep.signInStep}`);
    }

    await confirmSignIn({ challengeResponse: token });
    const user = await getCurrentUser();
    const properties = await loadUserProperties();

    return { user, properties };
};

export const loadUserProperties = async (): Promise<LoadUserPropertiesResult> => {
    const session: AuthSession = await fetchAuthSession();
    const payload = session?.tokens?.idToken?.payload;
    if (!payload) return { status: LoadUserPropertiesStatus.NO_SESSION };

    const result = cognitoTokenPayloadSchema.safeParse(payload);
    if (!result.success) {
        console.error(
            'Invalid Cognito token payload:',
            result.error.issues.map(({ code, path, message }) => ({ code, path, message }))
        );
        return { status: LoadUserPropertiesStatus.INVALID };
    }

    return { status: LoadUserPropertiesStatus.SUCCESS, data: result.data };
};

export const signOut = async (): Promise<void> => {
    await amplifySignOut();
};

export const getIdToken = async (): Promise<string | undefined> => {
    const session: AuthSession = await fetchAuthSession();

    return session.tokens?.idToken?.toString();
};

export const getAccessToken = async (): Promise<string | undefined> => {
    const session: AuthSession = await fetchAuthSession();

    return session.tokens?.accessToken?.toString();
};

export const getSession = async (): Promise<AuthSession> => {
    const session: AuthSession = await fetchAuthSession();

    return session;
};
